/**
 * Sync Jobs Hook
 *
 * Custom hook for managing and monitoring sync jobs
 * with real-time status updates.
 */

import { useCallback, useState } from 'react';

interface SyncJob {
  id: string;
  dataSourceId: string;
  type: 'full' | 'incremental';
  status: 'pending' | 'running' | 'completed' | 'failed';
  startedAt: string;
  completedAt?: string;
  recordsProcessed: number;
  recordsSkipped: number;
  errors: SyncError[];
  progress?: {
    current: number;
    total: number;
    phase: string;
  };
}

interface SyncError {
  timestamp: string;
  level: 'error' | 'warning';
  message: string;
  details?: any;
}

export const useSyncJobs = (dataSourceId?: string) => {
  const [syncJobs, setSyncJobs] = useState<SyncJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshJobs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Mock API call - in real app, would call actual API
      await new Promise(resolve => setTimeout(resolve, 500));

      const mockJobs: SyncJob[] = [
        {
          id: 'job_1',
          dataSourceId: 'example_ado_azure-devops',
          type: 'incremental',
          status: 'completed',
          startedAt: new Date(Date.now() - 300000).toISOString(), // 5 minutes ago
          completedAt: new Date(Date.now() - 60000).toISOString(), // 1 minute ago
          recordsProcessed: 150,
          recordsSkipped: 5,
          errors: [],
          progress: {
            current: 100,
            total: 100,
            phase: 'Completed',
          },
        },
        {
          id: 'job_2',
          dataSourceId: 'example_ado_azure-devops',
          type: 'full',
          status: 'running',
          startedAt: new Date(Date.now() - 120000).toISOString(), // 2 minutes ago
          recordsProcessed: 75,
          recordsSkipped: 2,
          errors: [],
          progress: {
            current: 75,
            total: 200,
            phase: 'Processing work items',
          },
        },
      ];

      // Filter by data source if specified
      const filteredJobs = dataSourceId
        ? mockJobs.filter(job => job.dataSourceId === dataSourceId)
        : mockJobs;

      setSyncJobs(filteredJobs);
    } catch (err: any) {
      setError(err.message || 'Failed to load sync jobs');
    } finally {
      setLoading(false);
    }
  }, [dataSourceId]);

  const startSync = useCallback(
    async (dataSourceId: string, type: 'full' | 'incremental') => {
      setLoading(true);
      setError(null);

      try {
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 500));

        const newJob: SyncJob = {
          id: `job_${Date.now()}`,
          dataSourceId,
          type,
          status: 'pending',
          startedAt: new Date().toISOString(),
          recordsProcessed: 0,
          recordsSkipped: 0,
          errors: [],
          progress: {
            current: 0,
            total: 100,
            phase: 'Initializing',
          },
        };

        setSyncJobs(prev => [newJob, ...prev]);

        // Simulate job progression
        setTimeout(() => {
          setSyncJobs(prev =>
            prev.map(job =>
              job.id === newJob.id
                ? { ...job, status: 'running' as const }
                : job
            )
          );
        }, 1000);

        return newJob.id;
      } catch (err: any) {
        setError(err.message || 'Failed to start sync job');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    syncJobs,
    loading,
    error,
    refreshJobs,
    startSync,
  };
};
